import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Flex, Space } from '@/components/base';
import Button from '@/components/Button/Button';
import ScrollView from '@/components/Common/ScrollView';
import Spinner from '@/components/Common/Spinner';
import { useModalContext } from '@/components/Modal/Modal';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import service from '@/services/index';
import { modalStyle } from '@/styles/style';

import type { G2Info, G2ProductInfo } from '../types';
import G2ProductItem from './G2ProductItem';

type Props = {
  workspace: G2Info | null;
  refetch: () => void;
};

const G2SettingModal = (props: Props) => {
  const color = useColor();
  const { closeModal } = useModalContext();

  const [g2Products, setG2Products] = useState<G2ProductInfo[] | null>(null);

  useEffect(() => {
    const handleProductInfos = async () => {
      if (!props.workspace) return;
      const products = await service.g2.getProudctInfos(props.workspace.alias);
      setG2Products(products);
    };
    handleProductInfos();
  }, [props.workspace]);

  const currentInfos = useMemo(() => {
    return props.workspace?.proudctInfos.map((info) => info.id) || [];
  }, [props.workspace]);

  const handleOnChangeActive = async (info: G2ProductInfo) => {
    if (!props.workspace) return;
    if (currentInfos.includes(info.id)) {
      await service.g2.pullG2ProductInfo(props.workspace.alias, info);
    } else {
      await service.g2.pushG2ProductInfo(props.workspace.alias, info);
    }
    props.refetch();
  };

  return (
    <ScrollView style={{ maxHeight: 'calc(100vh - 80px' }}>
      <Style d="column">
        <Heading type="h2" color={color.gray800}>
          G2 Products
        </Heading>
        <Space h="4px" />
        <Typo type="label2" color={color.gray700}>
          Select products to import reviews on Syncly
        </Typo>
        <Space h="24px" />
        <Flex className="channel-list-wrapper">
          {g2Products ? (
            <ScrollView>
              <Flex
                g="8px"
                j="space-between"
                style={{ flexWrap: 'wrap', width: '100%', padding: 24 }}
              >
                {g2Products.map((data) => (
                  <G2ProductItem
                    key={data.id}
                    isActive={currentInfos.includes(data.id)}
                    info={data}
                    onChangeActive={handleOnChangeActive}
                  />
                ))}
              </Flex>
            </ScrollView>
          ) : (
            <Flex
              a="center"
              j="center"
              style={{ width: '100%', height: '100%' }}
              d="column"
              g="16px"
            >
              <Spinner />
              <Typo type="label2" color={color.gray500}>
                Loading Product list...
              </Typo>
            </Flex>
          )}
        </Flex>
        <Space h="24px" />
        <Flex j="flex-end">
          <Button onClick={closeModal}>Done</Button>
        </Flex>
      </Style>
    </ScrollView>
  );
};

export default G2SettingModal;

const Style = styled(Flex)`
  padding: 24px;
  width: 800px;
  ${modalStyle};
  .channel-list-wrapper {
    height: 400px;
    border: 1px solid ${(props) => props.theme.colors.gray300};
    border-radius: 8px;
  }
`;
